import React, { useCallback, useMemo, useState } from 'react';
import { UserModel } from '../../../mobx/models/user';
import {
  Check,
  Invite,
  Members,
  ModalBlock,
  Name,
  Profile,
  Searchbar
} from '../../../pages/project/details/setting/styles';
import Avatar from '../../avatar';
import { ButtonSection } from './styles';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { ProjectModel } from '../../../mobx/models/project';
import { WorkspaceModel } from '../../../mobx/models/workspace';
import { addInvitedMember, getCurrentProjectDetails, removeInvitedMember } from '../../../services/projectServices';
import { PROJECT_ANALYTICS } from '../../../global/analyticsConstants';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { captureException } from '@sentry/react';
import { UsersWorkspaceInterface } from '../../../interfaces/WorkspaceInterface';
import { removeAgentFromProject , addAgentToProject } from '../../../services/workspaceService';
import { observer } from 'mobx-react-lite';
import { ProjectTemplateModel } from '../../../mobx/models/projectTemplate';
import { AVATAR_BORDER, TAB_BUTTON_TYPE, USER_ROLE } from '../../../global/constants';
import Tabs from '../../tabs';
import ModalHeader from '../modalHeader';
import CheckboxInput from '../../input/checkboxInput';
import Input from '../../input';

interface InviteMemberModalProps {
  onClose: () => void;
  customersUsers?: UserModel[];
  currentProject?: ProjectModel | ProjectTemplateModel;
  onSubModel: () => void;
  setLoading?: (value: boolean) => void;
  workspace?: WorkspaceModel;
  onClickMemberBox?: (userIndex: number, userItem: UserModel | UsersWorkspaceInterface) => void;
  isProjectTemplate?: boolean;
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  onClose,
  customersUsers = [],
  currentProject,
  onSubModel,
  setLoading,
  workspace,
  onClickMemberBox,
  isProjectTemplate
}) => {
  //State variables
  const [searchResult, setSearchResult] = useState<UserModel[]>([]);
  const [isSearch, setIsSearch] = useState(false);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('workspace');
  // Other variables
  const currentUserDetails = UserPreferenceSingleton.getInstance().getCurrentUser();

  const workspaceUsers = useMemo(() => {
    return workspace?.users?.filter((user) => user.role !== USER_ROLE.CLIENT);
  }, [workspace?.users]);

  const agentUsers = useMemo(() => {
    return workspace?.users?.filter((user) => user.id.startsWith('agent_')) || [];
  }, [workspace?.users]);

  const getRenderData = useMemo(() => {
    if (isSearch) {
      return searchResult;
    }
    return tab === 'workspace' ? workspaceUsers : tab === 'client' ? customersUsers : agentUsers;
  }, [agentUsers, customersUsers, isSearch, searchResult, tab, workspaceUsers]);

  // handles tab button Click
  const onClickTab = useCallback((e: React.SyntheticEvent, key?: string) => {
    e.preventDefault();
    if (key === 'client') {
      setTab('client');
    } else if (key === 'agent') {
      setTab('agent');
    } else {
      setTab('workspace');
    }
    setQuery('');
    setIsSearch(false);
  }, []);

  const searchQuery = useCallback(
    (value: string) => {
      let result;
      if (tab === 'client') {
        result = customersUsers?.filter((item) => item?.name?.toLowerCase().includes(value?.toLowerCase())) || [];
      } else if (tab === 'agent') {
        result = agentUsers?.filter((item) => item?.name?.toLowerCase().includes(value?.toLowerCase())) || [];
      } else {
        result = workspaceUsers?.filter((item) => item?.name?.toLowerCase().includes(value?.toLowerCase())) || [];
      }
      if (result) {
        setSearchResult(result);
        return null;
      }
    },
    [agentUsers, customersUsers, tab, workspaceUsers]
  );

  //Handle search input
  const onChangeSearch = useCallback(
    (e: { target: { value: string } }) => {
      const value = e.target.value;
      setQuery(value);
      if (value) {
        if (!isSearch) setIsSearch(true);
        searchQuery(value);
      } else {
        if (isSearch) setIsSearch(false);
        setSearchResult([]);
      }
    },
    [isSearch, searchQuery]
  );

  const handleCloseModel = useCallback(() => {
    onClose();
    setTab('workspace');
    setQuery('');
    setIsSearch(false);
  }, [onClose]);

  // handle member box click
  const handleMemberClickAction = useCallback(
    async (userIndex: number | undefined, invitedUser: UserModel | UsersWorkspaceInterface) => {
      const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
      try {
        if (currentProject instanceof ProjectTemplateModel) return;
        setLoading?.(true);
        let result;
        let usersClone = JSON.parse(JSON.stringify(currentProject?.users));
        if (invitedUser?.id.startsWith('agent_')) {
          if (userIndex === -1) {
            await addAgentToProject(invitedUser?.id, currentProject?.id || '');
          } else {
            await removeAgentFromProject(invitedUser?.id, currentProject?.id || '');
          }
        } else {
          if (userIndex === -1) {
            const payload = {
              userId: userDetails?.id,
              newUserId: invitedUser?.id,
              companyId: workspace?.id,
              projectId: currentProject?.id
            };
            usersClone = [...(usersClone || []), invitedUser];
            currentProject?.addUser(invitedUser?.id);
            result = await addInvitedMember(payload);
            trackAnalyticActivity(PROJECT_ANALYTICS.MEMBER_ADDED);
          } else {
            usersClone.splice(userIndex, 1);
            currentProject?.removeUser(invitedUser?.id);
            if (userDetails?.id) {
              result = await removeInvitedMember(userDetails?.id, currentProject?.id, invitedUser?.id);
              trackAnalyticActivity(PROJECT_ANALYTICS.MEMBER_REMOVED);
            }
          }
        }
        if (result) {
          await getCurrentProjectDetails(currentProject?.id);
        }
      } catch (e) {
        captureException(e);
      } finally {
        setLoading?.(false);
      }
    },
    [setLoading, currentProject, workspace?.id]
  );

  const renderTabButtons = useCallback(() => {
    const tabOptions = [
      {
        key: 'workspace',
        label: 'Workspace'
      },
      ...((customersUsers?.length ?? 0) > 0
        ? [
            {
              key: 'client',
              label: 'Client'
            }
          ]
        : []),
      ...(agentUsers?.length > 0
        ? [
            {
              key: 'agent',
              label: 'Agents'
            }
          ]
        : [])
    ];
    return tabOptions?.map((tabs) => {
      return (
        <Tabs
          key={tabs?.key}
          variant={TAB_BUTTON_TYPE.STANDARD}
          title={tabs?.label}
          size='small'
          onClick={(e: React.SyntheticEvent) => onClickTab(e, tabs?.key)}
          isActive={tabs?.key === tab}
        />
      );
    });
  }, [agentUsers.length, customersUsers.length, onClickTab, tab]);

  const renderMemberList = useCallback(
    (renderData: UserModel[] | UsersWorkspaceInterface[]) => {
      return (
        <ModalBlock>
          <Searchbar>
            <Input
              placeHolder='Search by name'
              value={query}
              onChange={onChangeSearch}
              autoFocus
              trailingIconName='search-icon'
              trailingIconViewBox='0 0 16 16'
            />
            {((customersUsers?.length ?? 0) > 0 || agentUsers.length > 0) && (
              <ButtonSection>{renderTabButtons()}</ButtonSection>
            )}
          </Searchbar>
          <Members>
            {renderData?.map((userItem) => {
              const userIndex = currentProject?.users?.findIndex((user) => user?.id === userItem?.id) ?? -1;
              return (
                <Profile
                  role='button'
                  key={userItem?.id}
                  onClick={() => {
                    if (currentUserDetails?.id === userItem.id && !isProjectTemplate) return;
                    onClickMemberBox
                      ? onClickMemberBox(userIndex, userItem)
                      : handleMemberClickAction(userIndex, userItem);
                  }}>
                  <Name>
                    <Avatar
                      imgSrc={userItem?.profile_photo || ''}
                      name={userItem?.name ? userItem.name : 'U N'}
                      size={28}
                      borderVariant={AVATAR_BORDER.NONE}
                    />
                    <p>{userItem?.name}</p>
                  </Name>
                  <Check>
                    <CheckboxInput
                      checked={userIndex === -1 ? false : true}
                      disabled={currentUserDetails?.id === userItem.id && !isProjectTemplate}
                    />
                  </Check>
                </Profile>
              );
            })}
          </Members>
          <Invite role='button' onClick={onSubModel}>
            Invite New Member
          </Invite>
        </ModalBlock>
      );
    },
    [
      query,
      onChangeSearch,
      customersUsers?.length,
      agentUsers.length,
      renderTabButtons,
      onSubModel,
      currentProject?.users,
      currentUserDetails?.id,
      isProjectTemplate,
      onClickMemberBox,
      handleMemberClickAction
    ]
  );

  return (
    <>
      <ModalHeader
        title={`Invite member to ${currentProject?.name} project.`}
        onClose={handleCloseModel}
        removeBottomBorder={true}
      />
      {renderMemberList(getRenderData || [])}
    </>
  );
};

export default observer(InviteMemberModal);
